<template>
    <div v-if="text" class="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm mt-8">
        <div class="flex justify-between items-center mb-4">
            <h3 class="text-xl font-bold text-gray-800">Esercizio Generato</h3>
            <div class="flex gap-2">
                <button @click="copyToClipboard"
                    class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copia
                </button>
                <button @click="downloadTxt"
                    class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    TXT
                </button>
                <button @click="downloadPdf"
                    class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                    </svg>
                    PDF
                </button>
            </div>
        </div>
        <div class="exercise-content prose max-w-none text-gray-700 font-sans">
            <h1 v-if="title" class="scenario-title">{{ title }}</h1>
            <div v-html="parsedMarkdown"></div>
        </div>
    </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import { marked } from "marked";

export default {
    name: "ExerciseDisplay",
    props: {
        text: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            default: '',
        },
        year: {
            type: String,
            default: '',
        },
        difficulty: {
            type: String,
            default: '',
        }
    },
    computed: {
        parsedMarkdown() {
            return marked(this.text);
        }
    },
    methods: {
        async copyToClipboard() {
            try {
                await navigator.clipboard.writeText(this.text);
                alert("Testo copiato negli appunti!");
            } catch (err) {
                console.error("Errore copia:", err);
                alert("Impossibile copiare il testo.");
            }
        },
        toPascalCase(str) {
            if (!str) return '';
            return str
                .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join('');
        },
        getYearString(year) {
            const map = { '3': 'Terza', '4': 'Quarta', '5': 'Quinta' };
            return map[year] || year;
        },
        getSanitizedFilename(extension) {
            // Desired format: EsercizioTitoloClasseDifficolta.pdf

            // 1. Process Title (PascalCase)
            let safeTitle = this.toPascalCase(this.title) || 'NonTitolato';

            // 2. Process Year
            let safeYear = this.getYearString(this.year);

            // 3. Process Difficulty (PascalCase)
            let safeDiff = this.toPascalCase(this.difficulty);

            // Construct filename
            const filename = `Esercizio${safeTitle}${safeYear}${safeDiff}`;

            return `${filename}.${extension}`;
        },
        downloadTxt() {
            const blob = new Blob([this.text], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = this.getSanitizedFilename('txt');
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },
        downloadPdf() {
            const element = this.$el.querySelector('.exercise-content');

            const opt = {
                margin: 15,
                filename: this.getSanitizedFilename('pdf'),
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };

            // html2pdf is imported from the library
            html2pdf().set(opt).from(element).save();
        },
    },
};
</script>

<style>
/* Graphic Layout Styles for Generated Content - Using Hex codes for html2canvas compatibility */
.exercise-content {
    color: #374151;
    /* text-gray-700 */
}

.exercise-content h1,
.exercise-content h2,
.exercise-content h3 {
    color: #1e40af;
    /* text-blue-800 */
    font-weight: 700;
    margin-bottom: 1rem;
    margin-top: 1.5rem;
    border-bottom: 1px solid #dbeafe;
    /* border-blue-100 */
    padding-bottom: 0.5rem;
}

.exercise-content h1 {
    font-size: 1.5rem;
    line-height: 2rem;
}

.exercise-content h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
}

.exercise-content h3 {
    font-size: 1.125rem;
    line-height: 1.75rem;
}

.exercise-content strong {
    color: #1e3a8a;
    /* text-blue-900 */
    font-weight: 700;
    background-color: #eff6ff;
    /* bg-blue-50 */
    padding-left: 0.25rem;
    padding-right: 0.25rem;
    border-radius: 0.25rem;
}

.exercise-content ul {
    list-style-type: disc;
    padding-left: 1.25rem;
    margin-bottom: 1rem;
}

.exercise-content ul>li {
    margin-bottom: 0.5rem;
}

.exercise-content ol {
    list-style-type: decimal;
    padding-left: 1.25rem;
    margin-bottom: 1rem;
    background-color: #f9fafb;
    /* bg-gray-50 */
    padding: 1rem;
    border-radius: 0.5rem;
    border: 1px solid #f3f4f6;
    /* border-gray-100 */
}

.exercise-content ol>li {
    margin-bottom: 0.5rem;
}

.exercise-content p {
    margin-bottom: 1rem;
    line-height: 1.625;
}

.exercise-content hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin-top: 2rem;
    margin-bottom: 1.5rem;
}

.scenario-title {
    text-align: center;
    font-size: 2rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 2rem !important;
}
</style>
